import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { renewToken, forceLogout, isLoggedOutLocked, isHardAuthFailure } from "./AuthService";
import { isPublicRoute } from "./publicRoutes";
// Static import: `src/i18n.js` pulls in i18next and its HTTP backend only, never
// app modules, so there is no cycle back through request.ts. It is needed because
// the subscription-restricted 403 has to be explained in the user's language from
// inside an interceptor, where React hooks are unavailable.
import i18n from "../i18n";
import { AI_QUOTA_ERROR_CODE, RATE_LIMITED_ERROR_CODE, formatUtcReset, hoursUntilReset } from "./aiQuota";

// Axios response-error handler.
//
// 401 path:
//   1. If the original request has already been retried, or we're already
//      tearing down, fail fast (fire-and-forget forceLogout) so callers
//      don't hang waiting for the logout redirect.
//   2. Dedupe concurrent 401s against a single in-flight renewal promise.
//   3. On renewal success, trust the returned user's access_token directly
//      for the retry — don't re-read Redux, which may not have absorbed the
//      addUserLoaded dispatch yet.
//   4. On renewal failure or a still-401 retry, fire forceLogout and reject
//      to the caller.
//
// Non-401 path preserves the prior body-parsing and the OnboardingBlocked
// redirect that the app depends on.

type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<any> | null = null;
let onboardingRedirectInFlight = false;

function onCallbackRoute(): boolean {
    try {
        const p = (window.location?.pathname || '').toLowerCase();
        return p.startsWith('/logincallback') || p.startsWith('/silent-renew') || p.startsWith('/logoutcallback');
    } catch {
        return false;
    }
}

async function parseErrorBody(response: any): Promise<any> {
    // An empty body carries no information, so hand back the response itself — callers need
    // its `status` to tell "you may not do this" apart from "this failed, try again".
    // ASP.NET authorization denials (403) have no body, and returning the empty string here
    // left screens unable to distinguish the two, so they showed a misleading "try again".
    if (!response || response.data == null || response.data === '') return response;
    let result = response.data;
    if (result instanceof Blob) {
        if (result.size === 0) return response;
        if (result.type === 'application/json') {
            try {
                const text = await result.text();
                result = JSON.parse(text);
            } catch {
                return result;
            }
        }
    }
    return result;
}

/**
 * CO-56 — preserve the HTTP status in the value we reject with.
 *
 * ASP.NET's authorization middleware writes a BODILESS 403, so axios sets
 * `response.data = ""`. Rejecting the bare `""` threw the status away, and
 * `classifyWriteError` could then never see 403/409/5xx: every empty-bodied
 * refusal rendered as `unknown` ("Save failed") with no reason. The
 * `permission` / `conflict` / `server` classifications were unreachable for
 * any endpoint that does not hand-write a JSON body.
 *
 * CO-56 REPAIR — the transport status lives in its OWN namespace, `__httpStatus`.
 * The first cut attached the real status to `status` only when the body didn't
 * already carry one, which defeated the fix for the most common case: this
 * server's `Result<TPayload>.Status` DEFAULTS TO 200 (see
 * `Data/General/View Models/Result.cs`) and `Result.Ok<T>()` never sets it. So
 * every endpoint that returns a default-constructed Result under an HTTP 4xx/5xx
 * shipped a body-level `status: 200`, the real status was skipped, and the
 * response classified as validation/unknown again. A body's self-reported status
 * is not evidence about the transport, so the two are now kept apart and
 * `__httpStatus` is attached UNCONDITIONALLY.
 *
 * Rules:
 *  - Object bodies (the common `Result<T>` / ProblemDetails shape) are handed
 *    back UNCHANGED in every enumerable respect — `__httpStatus` is attached as
 *    a NON-ENUMERABLE property, so spreads, `Object.keys` and JSON round-trips
 *    are unaffected. A legacy convenience `status` is still filled in only when
 *    the body has none (for `utils/errors.ts`, which reads `e.status`).
 *  - Primitive / empty / array bodies are wrapped, because there is nowhere on
 *    them to hang a status. EVERY field of that wrapper is non-enumerable and it
 *    carries a `toJSON` that returns the original body, so a consumer that
 *    stringifies the caught error still sees exactly what it saw before the fix
 *    (`""` for an empty 403 body) instead of a raw JSON blob.
 *  - When there is no response at all (a true offline/DNS failure) this is not
 *    called, so a genuine network error still classifies as `network`.
 */
function hide(target: any, props: Record<string, unknown>): any {
    for (const [key, value] of Object.entries(props)) {
        try {
            Object.defineProperty(target, key, {
                value,
                enumerable: false,
                configurable: true,
                writable: true,
            });
        } catch {
            /* frozen/sealed body — leave it alone rather than throw from the interceptor */
        }
    }
    return target;
}

/**
 * CO-226 — THE STRUCTURED REFUSAL MUST SURVIVE THE REJECTION.
 *
 * This interceptor rejects EVERY non-2xx, so a refusal the server took the trouble to give a
 * machine-readable `code` (`PLAN_EMPLOYEE_CAP_REACHED`, `OnboardingBlocked`, `feature_locked`,
 * `subscription_restricted`, the billing `SUBSCRIPTION_*` denials…) arrives in the caller's `catch`,
 * not in its resolved-value branch. Any screen that only switched on the code where the promise
 * RESOLVES therefore never ran that switch at all, and the user got the generic red "it failed"
 * banner for a refusal the product knows how to explain and fix. That is the CO-226 defect, and it
 * is a SHAPE bug, not a one-code bug — every caller reading `errors[0].code` off a 4xx has it.
 *
 * Two halves to the repair:
 *  1. `withApiErrors` stamps the normalised `errors[]` onto the rejected value as a NON-ENUMERABLE
 *     `__apiErrors`, so it is present even when the body had to be wrapped (empty/primitive bodies
 *     have nowhere to hang anything) and even after the wrapper hides the body under `data`.
 *     Non-enumerable is load-bearing: spreads, `Object.keys`, `JSON.stringify` and every existing
 *     consumer see exactly the value they saw before.
 *  2. `apiErrorsOf` / `findApiError` / `hasApiErrorCode` read the code back off ANY failure shape a
 *     caller can be handed — a resolved `Result<T>`, the rejected body, the wrapper, or a raw
 *     `AxiosError` from a caller that bypassed this interceptor. Callers branch on the code once
 *     and stop caring which path delivered it.
 *
 * Nothing here throws: a helper that can throw inside a `catch` block turns a handled refusal into
 * an unhandled crash.
 */
export interface ApiErrorItem {
    code?: string;
    description?: string;
    details?: unknown;
    [key: string]: unknown;
}

function normaliseErrorArray(candidate: unknown): ApiErrorItem[] | null {
    if (!Array.isArray(candidate)) return null;
    const items = candidate.filter((e): e is ApiErrorItem => !!e && typeof e === 'object');
    return items.length > 0 ? items : null;
}

/**
 * The middleware refusals (`TierEnforcementMiddleware`, the billing gate) answer with a top-level
 * `{ error: "feature_locked" }` rather than the `Result<T>.errors[]` envelope. Presenting both as
 * one list is what lets a caller write a single code switch.
 */
function normaliseTopLevelError(source: Record<string, unknown>): ApiErrorItem[] | null {
    const code = source.error;
    if (typeof code !== 'string' || code.length === 0) return null;
    const message = source.message;
    return [{ code, description: typeof message === 'string' ? message : undefined, details: source }];
}

/** Every failure shape this client can be handed, flattened to `errors[]`. Never throws. */
export function apiErrorsOf(source: unknown): ApiErrorItem[] {
    try {
        const seen = new Set<unknown>();
        let node: unknown = source;
        // Walk the wrappers: rejected body → `data` (withHttpStatus wrapper / axios response)
        // → `response.data` (a raw AxiosError). Bounded by `seen` so a self-referential body
        // cannot spin here.
        for (let depth = 0; node && typeof node === 'object' && depth < 5; depth++) {
            if (seen.has(node)) break;
            seen.add(node);
            const bag = node as Record<string, unknown>;
            const stamped = normaliseErrorArray(bag.__apiErrors);
            if (stamped) return stamped;
            const envelope = normaliseErrorArray(bag.errors);
            if (envelope) return envelope;
            const topLevel = normaliseTopLevelError(bag);
            if (topLevel) return topLevel;
            const nested = bag.response as Record<string, unknown> | undefined;
            node = bag.data ?? nested?.data ?? nested;
        }
    } catch {
        /* a helper called from a catch block must never throw */
    }
    return [];
}

/** The first error carrying one of `codes`, or null. Case-sensitive — codes are contracts. */
export function findApiError(source: unknown, ...codes: string[]): ApiErrorItem | null {
    if (codes.length === 0) return null;
    return apiErrorsOf(source).find((e) => typeof e.code === 'string' && codes.includes(e.code)) ?? null;
}

/** True when `source` carries `code`, whatever shape it arrived in. */
export function hasApiErrorCode(source: unknown, code: string): boolean {
    return findApiError(source, code) != null;
}

/**
 * CO-577 — the ONE way to read a transport status off a rejection.
 *
 * `__httpStatus` is stamped NON-ENUMERABLY by the error interceptor and is the only trustworthy
 * source. A body's own `status` is deliberately NOT consulted: `Result<TPayload>.Status` defaults to
 * 200 and `Result.Ok<T>()` never sets it (CO-56, see the comment at the top of this file), so reading
 * it would report 200 for a 401 or a 429. `response.status` is kept only for a foreign axios error
 * that never reached the interceptor.
 *
 * Same contract as the two file-local versions in `api/PreformanceManagement/PerformanceSignalsAi.ts`
 * and `api/Navigation/moduleConfig.ts`, which this is intended to eventually absorb.
 *
 * Returns undefined when no trustworthy status is available — including the interceptor's four 401
 * early-return paths, which reject via `parseErrorBody` without stamping. Undefined is correct there:
 * a caller falling back to a generic message is right, and a confidently WRONG 200 is not.
 */
export function httpStatusOf(err: unknown): number | undefined {
    const e = (err ?? {}) as {
        __httpStatus?: unknown;
        response?: { status?: unknown } | null;
    };
    for (const candidate of [e.__httpStatus, e.response?.status]) {
        if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
    }
    return undefined;
}

function withApiErrors<T>(target: T, body: unknown): T {
    const errors = apiErrorsOf(body);
    if (errors.length > 0 && target && typeof target === 'object') {
        hide(target, { __apiErrors: errors });
    }
    return target;
}

function withHttpStatus(response: any, result: any): any {
    const status = response?.status;
    if (typeof status !== 'number') return withApiErrors(result, result);

    if (result && typeof result === 'object' && !Array.isArray(result)) {
        hide(result, { __httpStatus: status });
        if ((result as { status?: unknown }).status == null) {
            hide(result, { status });
        }
        return withApiErrors(result, result);
    }

    // NOT an axios response — it deliberately has no `request`, so
    // isNetworkShape() cannot mistake it for an offline failure.
    return withApiErrors(hide({}, {
        __httpStatus: status,
        status,
        statusText: response?.statusText,
        // Keep the original body under `data` so nothing is lost.
        data: result,
        __httpError: true,
        // Preserve the pre-CO-56 stringification of the rejected value.
        toJSON: () => result,
    }), result);
}

// Module-level lock-toast guard. The backend TierEnforcementMiddleware
// returns `{ error: "feature_locked", ... }` on 403 for any locked-tier API.
// Several queries can 403 simultaneously (e.g. a dashboard loading multiple
// widgets), so we throttle the toast/redirect so the user sees a single,
// helpful message instead of a cascade.
let featureLockedToastInFlight = false;

// Background/global polls that ALL users (including Free) make at boot or on a
// timer. The user didn't navigate to attendance — these calls just happen to
// touch a tier-locked endpoint as a side-effect of the app initializing. Don't
// fire the global "feature isn't available" toast for these, or every Free user
// sees an unsolicited upgrade nag on every page load.
const SUPPRESS_TOAST_PATHS: readonly RegExp[] = [
    /shift\/AnyUserWithVariableScheduleRule/i,
];

function maybeShowFeatureLockedToast(response: unknown, result: unknown): void {
    const status = (response as { status?: number } | null | undefined)?.status;
    if (status !== 403) return;
    // Pattern A: structured top-level body { error: "feature_locked", ... }.
    // Pattern B: shaped through Result<T>.errors[].code === "feature_locked"
    //   (we don't currently emit this, but tolerate it for forward-compat).
    const topLevel = (result && typeof result === 'object')
        ? (result as { error?: unknown; errors?: unknown })
        : null;
    const errsRaw = topLevel && Array.isArray(topLevel.errors) ? topLevel.errors : [];
    const errsArr = errsRaw as Array<{ code?: unknown } | null | undefined>;
    const isLocked =
        (topLevel && topLevel.error === 'feature_locked') ||
        errsArr.some((e) => e?.code === 'feature_locked' || e?.code === 'FeatureLocked');
    if (!isLocked) return;

    // Skip the toast for background polls (see SUPPRESS_TOAST_PATHS above).
    const requestUrl =
        ((response as { config?: { url?: string; baseURL?: string } } | null | undefined)?.config?.url) ?? '';
    if (SUPPRESS_TOAST_PATHS.some((re) => re.test(requestUrl))) return;

    if (featureLockedToastInFlight) return;
    if (typeof window === 'undefined') return;

    // Lazy require so this module stays free of side-effects when not used.
    try {
        const url = window.location?.pathname ?? '';
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const analytics = require('./analytics') as typeof import('./analytics');
        analytics.track(analytics.AnalyticsEvents.FeatureLockedHit, { path: url });
    } catch {
        /* analytics best-effort */
    }

    // 🔴 THIS TOAST WAS ENGLISH ON EVERY LOCALE. The subscription-restricted toast below has been
    // translated from the start; the upsell path beside it was not, so an Arabic or French tenant
    // hit a paywall and was told about it — and offered the trial — in English. Same guard as
    // there: never throw from an interceptor, so a key that cannot be resolved (i18n not
    // initialised yet on a very early request) degrades to the key rather than taking down the
    // whole error path. The `common` namespace is the default, so no `ns:` prefix is needed here.
    const translate = (key: string): string => {
        try {
            const text = i18n.t(key);
            return typeof text === 'string' && text.length > 0 ? text : key;
        } catch {
            return key;
        }
    };

    featureLockedToastInFlight = true;
    try {
        // Lightweight inline toast — we can't use the React useToast hook from
        // an axios interceptor, so we render a single DOM element that the
        // user can dismiss. The toast offers an upgrade path; clicking it navigates to
        // /settings/billing (HR/Admin-protected at the route). CO-227: was /start-trial.
        const toast = document.createElement('div');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.style.cssText = [
            'position:fixed',
            'bottom:1rem',
            'inset-inline-end:1rem',
            'z-index:9999',
            'display:flex',
            'align-items:center',
            'gap:0.75rem',
            'background:#1e293b',
            'color:white',
            'padding:0.75rem 1rem',
            'border-radius:0.5rem',
            'box-shadow:0 10px 25px -5px rgba(0,0,0,0.25)',
            'max-width:min(28rem, 90vw)',
            'font:500 0.875rem/1.25rem ui-sans-serif,system-ui,sans-serif',
        ].join(';');

        const message = document.createElement('span');
        message.textContent = translate('featureLockedToast.message');
        toast.appendChild(message);

        const action = document.createElement('button');
        action.type = 'button';
        action.textContent = translate('featureLockedToast.action');
        action.style.cssText = [
            'background:#f59e0b',
            'color:#1e293b',
            'border:0',
            'padding:0.35rem 0.75rem',
            'border-radius:0.375rem',
            'font-weight:600',
            'cursor:pointer',
            'white-space:nowrap',
        ].join(';');
        action.addEventListener('click', () => {
            try {
                window.history.pushState({}, '', '/settings/billing');
                window.dispatchEvent(new PopStateEvent('popstate'));
            } catch {
                window.location.assign('/settings/billing');
            }
            cleanup();
        });
        toast.appendChild(action);

        const close = document.createElement('button');
        close.type = 'button';
        close.setAttribute('aria-label', translate('featureLockedToast.dismiss'));
        close.textContent = '×';
        close.style.cssText = 'background:transparent;border:0;color:white;font-size:1.25rem;line-height:1;cursor:pointer;padding:0 0.25rem;';
        close.addEventListener('click', () => cleanup());
        toast.appendChild(close);

        const cleanup = () => {
            try { toast.remove(); } catch { /* ignore */ }
            featureLockedToastInFlight = false;
        };

        document.body.appendChild(toast);
        // Auto-dismiss after 7s so it doesn't linger if the user ignores it.
        setTimeout(cleanup, 7000);
    } catch {
        // If DOM access fails for any reason, just unlock the flag so a
        // future 403 can try again — never throw from the interceptor.
        featureLockedToastInFlight = false;
    }
}

// Module-level guard for the subscription-restricted toast, mirroring the
// feature-locked one above: a page that fires five writes at once must not
// stack five identical bands.
let restrictedToastInFlight = false;

const BILLING_ROLES = ['Hr', 'Admin'];

type AppStoreLike = { getState?: () => { user?: { role?: string } } };

function currentRole(): string {
    try {
        const store = (globalThis as unknown as { __APP_STORE__?: AppStoreLike }).__APP_STORE__;
        const role = store?.getState?.()?.user?.role;
        return typeof role === 'string' ? role : '';
    } catch {
        return '';
    }
}

/**
 * The backend returns `{ error: "subscription_restricted", accessState, graceEndedAt,
 * payUrl }` on 403 when a company's payment has lapsed past the grace window and a
 * WRITE is attempted. Reads and exports are never blocked, so this only ever fires
 * on an action the user deliberately took.
 *
 * THIS MUST NEVER SURFACE AS A GENERIC ERROR. A 403 that is indistinguishable from
 * a fault is exactly the defect that made the mobile client swallow these silently;
 * the user is left thinking the product is broken when in fact one invoice is
 * unpaid and a single click fixes it. So the toast:
 *
 *   - says specifically what happened, translated, in the user's language;
 *   - says that reading and exporting still work, because the first fear is
 *     "have we lost our payroll data";
 *   - offers Pay Now ONLY to HR/Admin. An employee has no billing surface by design
 *     and must not be sent to a route that will bounce them; they are told to ask
 *     their HR or Admin team instead.
 */
function maybeShowSubscriptionRestrictedToast(response: unknown, result: unknown): void {
    const status = (response as { status?: number } | null | undefined)?.status;
    if (status !== 403) return;

    const topLevel = (result && typeof result === 'object')
        ? (result as { error?: unknown; errors?: unknown; payUrl?: unknown })
        : null;
    const errsRaw = topLevel && Array.isArray(topLevel.errors) ? topLevel.errors : [];
    const errsArr = errsRaw as Array<{ code?: unknown } | null | undefined>;
    const isRestricted =
        (topLevel && topLevel.error === 'subscription_restricted') ||
        errsArr.some((e) => e?.code === 'subscription_restricted' || e?.code === 'SubscriptionRestricted');
    if (!isRestricted) return;

    if (restrictedToastInFlight) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const canPay = BILLING_ROLES.includes(currentRole());
    const payUrl = typeof topLevel?.payUrl === 'string' && topLevel.payUrl.startsWith('/')
        ? topLevel.payUrl
        : '/settings/billing';

    // Never throw from an interceptor: if i18n has not initialised yet, fall back
    // to the raw key rather than taking down the error path itself.
    const translate = (key: string): string => {
        try {
            const text = i18n.t(key);
            return typeof text === 'string' && text.length > 0 ? text : key;
        } catch {
            return key;
        }
    };

    restrictedToastInFlight = true;
    try {
        const toast = document.createElement('div');
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        toast.setAttribute('data-testid', 'subscription-restricted-toast');
        toast.style.cssText = [
            'position:fixed',
            'bottom:1rem',
            'inset-inline-end:1rem',
            'z-index:9999',
            'display:flex',
            'align-items:flex-start',
            'gap:0.75rem',
            'background:#1e293b',
            'color:white',
            'padding:0.875rem 1rem',
            'border-radius:0.5rem',
            'box-shadow:0 10px 25px -5px rgba(0,0,0,0.25)',
            'max-width:min(32rem, 92vw)',
            'font:500 0.875rem/1.35 ui-sans-serif,system-ui,sans-serif',
        ].join(';');

        const message = document.createElement('span');
        message.textContent = translate(
            canPay ? 'billing:restrictedToast.message' : 'billing:restrictedToast.messageEmployee',
        );
        toast.appendChild(message);

        const cleanup = () => {
            try { toast.remove(); } catch { /* ignore */ }
            restrictedToastInFlight = false;
        };

        if (canPay) {
            const action = document.createElement('button');
            action.type = 'button';
            action.textContent = translate('billing:restrictedToast.action');
            action.style.cssText = [
                'background:#f59e0b',
                'color:#1e293b',
                'border:0',
                'padding:0.35rem 0.75rem',
                'border-radius:0.375rem',
                'font-weight:600',
                'cursor:pointer',
                'white-space:nowrap',
                'flex-shrink:0',
            ].join(';');
            action.addEventListener('click', () => {
                try {
                    window.history.pushState({}, '', payUrl);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                } catch {
                    window.location.assign(payUrl);
                }
                cleanup();
            });
            toast.appendChild(action);
        }

        const close = document.createElement('button');
        close.type = 'button';
        close.setAttribute('aria-label', translate('billing:restrictedToast.dismiss'));
        close.textContent = '×';
        close.style.cssText = 'background:transparent;border:0;color:white;font-size:1.25rem;line-height:1;cursor:pointer;padding:0 0.25rem;flex-shrink:0;';
        close.addEventListener('click', () => cleanup());
        toast.appendChild(close);

        document.body.appendChild(toast);
        // Longer than the feature-locked toast: this one carries an action the
        // user is expected to read and decide on, not just an FYI.
        setTimeout(cleanup, 12000);
    } catch {
        restrictedToastInFlight = false;
    }
}

// Module-level guard for the AI-quota toast, mirroring the two 403 toasts above.
let aiQuotaToastInFlight = false;

/**
 * CO-225 — the daily AI request quota is spent.
 *
 * THERE WAS NO 429 BRANCH IN THIS FILE AT ALL. Every 429 fell through to the generic reject, and
 * because the burst limiters emitted a BODILESS 429 (Program.cs set RejectionStatusCode with no
 * OnRejected), the SPA could not have told the two apart even if it had looked. Both now carry a
 * body: `ai_quota_exceeded` for the daily cap, `rate_limited` for a burst.
 *
 * The two get different copy on purpose. A burst limit clears in under a minute and the user should
 * simply retry. The daily cap does not clear until UTC midnight, which for a Riyadh tenant is 3am
 * local — so the toast names the reset time IN UTC, explicitly, and adds "in about N hours" because
 * a bare UTC timestamp is not something a person can act on. It must never say "midnight".
 */
function maybeShowRateLimitToast(response: unknown, result: unknown): void {
    const status = (response as { status?: number } | null | undefined)?.status;
    if (status !== 429) return;

    const body = (result && typeof result === 'object')
        ? (result as { error?: unknown; used?: unknown; limit?: unknown; resetsAtUtc?: unknown })
        : null;

    const isQuota = body?.error === AI_QUOTA_ERROR_CODE;
    const isBurst = body?.error === RATE_LIMITED_ERROR_CODE;
    // A 429 we don't recognise is left to the caller rather than mislabelled.
    if (!isQuota && !isBurst) return;

    if (aiQuotaToastInFlight) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Never throw from an interceptor — fall back to the raw key if i18n isn't ready.
    const translate = (key: string, vars?: Record<string, unknown>): string => {
        try {
            const text = i18n.t(key, vars as never);
            return typeof text === 'string' && text.length > 0 ? text : key;
        } catch {
            return key;
        }
    };

    let message: string;
    if (isQuota) {
        const limit = typeof body?.limit === 'number' ? body.limit : 0;
        const resetsAtUtc = typeof body?.resetsAtUtc === 'string' ? body.resetsAtUtc : '';
        // CO-355 — the refusal now carries a REMEDY as well as the facts. Naming the cap and the
        // reset time (CO-225) told the user what happened; it still left them with nothing to do
        // about it. The second sentence is deliberately role-neutral — an employee cannot reach the
        // read-only quota page, so pointing at a screen would be a dead end for most of the people
        // who see this toast.
        message = translate('ai.quota.exceededToast', {
            limit,
            resetsAt: formatUtcReset(resetsAtUtc, i18n.language),
            hours: hoursUntilReset(resetsAtUtc),
        }) + ' ' + translate('ai.quota.exceededRemedy');
    } else {
        message = translate('ai.quota.burstToast');
    }

    aiQuotaToastInFlight = true;
    try {
        const toast = document.createElement('div');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.setAttribute('data-testid', 'ai-quota-toast');
        toast.style.cssText = [
            'position:fixed',
            'bottom:1rem',
            'inset-inline-end:1rem',
            'z-index:9999',
            'display:flex',
            'align-items:flex-start',
            'gap:0.75rem',
            'background:#1e293b',
            'color:white',
            'padding:0.875rem 1rem',
            'border-radius:0.5rem',
            'box-shadow:0 10px 25px -5px rgba(0,0,0,0.25)',
            'max-width:min(32rem, 92vw)',
            'font:500 0.875rem/1.35 ui-sans-serif,system-ui,sans-serif',
        ].join(';');

        const text = document.createElement('span');
        text.textContent = message;
        toast.appendChild(text);

        const cleanup = () => {
            try { toast.remove(); } catch { /* ignore */ }
            aiQuotaToastInFlight = false;
        };

        const close = document.createElement('button');
        close.type = 'button';
        close.setAttribute('aria-label', translate('billing:restrictedToast.dismiss'));
        close.textContent = '×';
        close.style.cssText = 'background:transparent;border:0;color:white;font-size:1.25rem;line-height:1;cursor:pointer;padding:0 0.25rem;flex-shrink:0;';
        close.addEventListener('click', () => cleanup());
        toast.appendChild(close);

        document.body.appendChild(toast);
        setTimeout(cleanup, isQuota ? 12000 : 7000);
    } catch {
        aiQuotaToastInFlight = false;
    }
}

function maybeRedirectToOnboarding(response: any, result: any): void {
    // Parallel dashboard queries can all 403 within the same tick. Without
    // this guard, each response fires window.location.assign, piling up
    // aborted navigations and producing a reload storm users read as an
    // "infinite loop". Only the first 403 in a batch should redirect.
    if (onboardingRedirectInFlight) return;
    try {
        const errs = (result as any)?.errors;
        const isBlocked = response?.status === 403 && Array.isArray(errs)
            && errs.some((e: any) => e?.code === 'OnboardingBlocked');
        if (isBlocked && typeof window !== 'undefined' && window.location) {
            const path = window.location.pathname || '';
            // The Pending Actions inbox is an allowed destination for a blocked
            // user (it lists the very terms/onboarding items they must resolve —
            // see OnboardingGate.ALLOWED_PATHS). Its MasterLayout still fires
            // background queries (subscription, platform-terms, shift checks)
            // that legitimately 403 while blocked; those must not bounce the
            // user off the inbox, or the "resolve it from the inbox" flow breaks.
            if (!path.startsWith('/onboarding/') && !path.startsWith('/terms-and-policies/') && !path.startsWith('/pending-actions')) {
                onboardingRedirectInFlight = true;
                window.location.assign('/onboarding/complete');
            }
        }
    } catch {
        // ignore redirect failures
    }
}

const errorHandling = async (error: AxiosError): Promise<any> => {
    const response = error.response as any;
    const originalRequest = error.config as RetryConfig | undefined;

    // ---- 401: attempt reactive token renewal ----
    if (response?.status === 401 && originalRequest) {
        // Skip renewal on OIDC callback routes — those handle their own
        // auth state; a forceLogout here would fight the callback.
        if (onCallbackRoute()) {
            return Promise.reject(await parseErrorBody(response));
        }

        // Never hijack an anonymous visitor on a public route (e.g. /p/<slug>).
        // A stale token in localStorage can cause a background fetch to 401;
        // forceLogout → signinRedirect would bounce the visitor to
        // /Account/Login, which is exactly the bug this guard prevents.
        if (isPublicRoute()) {
            return Promise.reject(await parseErrorBody(response));
        }

        // Already retried once, or a logout is already in flight → give up.
        if (originalRequest._retry || isLoggedOutLocked()) {
            void forceLogout('retry-after-renew-still-401');
            return Promise.reject(await parseErrorBody(response));
        }

        if (!refreshPromise) {
            refreshPromise = renewToken().finally(() => {
                refreshPromise = null;
            });
        }

        try {
            const user = await refreshPromise;
            if (!user?.access_token) {
                void forceLogout('renew-returned-no-token');
                return Promise.reject(await parseErrorBody(response));
            }
            originalRequest._retry = true;
            originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${user.access_token}`,
            };
            return axios(originalRequest);
        } catch (renewErr) {
            // Only log the user out when the IdP tells us the session is
            // truly gone (login_required / interaction_required / invalid_grant).
            // Transient failures — iframe timeout, network blip, backend
            // cold-starting, CORS on silent-renew.html — must NOT force logout;
            // they leave the stale token in place and the next 401 will try
            // again. Forcing logout on transient errors is the bug that kicks
            // users out on flaky wifi or during a 10-second redeploy.
            if (isHardAuthFailure(renewErr)) {
                void forceLogout('renew-threw-hard');
            } else {
                console.warn('[auth] silent-renew threw a transient error; leaving session in place:', renewErr);
            }
            return Promise.reject(renewErr);
        }
    }

    // ---- Non-401: preserve body parsing + OnboardingBlocked redirect ----
    if (response && response.data != null && !(response.data instanceof Blob && response.data.size === 0)) {
        const result = await parseErrorBody(response);
        maybeRedirectToOnboarding(response, result);
        // Phase 5: surface the backend tier-lock with a single toast so direct
        // API consumers (cached pages, deep-link navigations) get a clear
        // explanation instead of a silent 403.
        maybeShowFeatureLockedToast(response, result);
        // CO-05: a lapsed payment blocks writes. Explain it specifically, with a
        // Pay Now path for HR/Admin — never a generic failure.
        maybeShowSubscriptionRestrictedToast(response, result);
        // CO-225: 429 — tell the daily AI quota apart from a burst limit, and explain both.
        maybeShowRateLimitToast(response, result);
        // CO-56: carry the status with the body, or an empty 403 classifies as `unknown`.
        return Promise.reject(withHttpStatus(response, result));
    }

    // A response with a genuinely null body: reject with a status-carrying
    // shape rather than the raw axios response. The raw response has `.request`,
    // which made isNetworkShape() match and mislabel an RBAC refusal as
    // "check your connection" (CO-56, second half).
    if (response) return Promise.reject(withHttpStatus(response, null));

    // No response at all — a real network/offline failure. Reject the axios
    // error untouched so it still classifies as `network`.
    return Promise.reject(error);
};

export default errorHandling;
